import { icoContact, legalContact } from "../legalContact";

// Section numbers are supplied by the renderer from the array index, so they are not
// stored here — the heading, the table of contents entry and the anchor cannot drift apart.
const privacyPolicy = {
  eyebrow: "Legal",
  title: "Privacy Policy",
  summary:
    "How AV Facade Services Ltd collects, uses, stores and protects your personal information.",
  effectiveDate: "1 July 2026",
  lastUpdated: "1 July 2026",
  intro: [
    {
      type: "paragraph",
      text: 'AV Facade Services Ltd ("we", "our", or "us") is committed to protecting your privacy and ensuring that your personal information is handled responsibly.',
    },
    {
      type: "paragraph",
      text: "This Privacy Policy explains how we collect, use, store and protect your personal information when you visit our website or contact us.",
    },
  ],
  sections: [
    {
      id: "who-we-are",
      title: "Who We Are",
      blocks: [
        { type: "address" },
        {
          type: "paragraph",
          text: "We are a specialist façade installation contractor providing labour-only services including:",
        },
        {
          type: "list",
          items: [
            "Curtain Wall Installation",
            "Cladding Installation",
            "Remedial Works",
            "Aluminium Windows & Doors",
            "Commercial Façade Solutions",
          ],
        },
      ],
    },
    {
      id: "information-we-collect",
      title: "Information We Collect",
      blocks: [
        { type: "paragraph", text: "We may collect the following information:" },
        {
          type: "subsection",
          title: "Information you provide",
          blocks: [
            {
              type: "list",
              items: [
                "Name",
                "Company name",
                "Email address",
                "Telephone number",
                "Project information",
                "Messages submitted through our contact form",
              ],
            },
          ],
        },
        {
          type: "subsection",
          title: "Information collected automatically",
          blocks: [
            { type: "paragraph", text: "When you visit our website we may collect:" },
            {
              type: "list",
              items: [
                "IP address",
                "Browser type",
                "Device information",
                "Pages visited",
                "Time spent on the website",
                "Referring website",
              ],
            },
            {
              type: "paragraph",
              text: "This information helps us improve our website and services.",
            },
          ],
        },
      ],
    },
    {
      id: "how-we-use-your-information",
      title: "How We Use Your Information",
      blocks: [
        { type: "paragraph", text: "We use your information to:" },
        {
          type: "list",
          items: [
            "Respond to enquiries",
            "Provide quotations",
            "Deliver our services",
            "Communicate regarding projects",
            "Improve our website",
            "Maintain website security",
            "Meet legal obligations",
          ],
        },
        {
          type: "paragraph",
          text: "We only process personal data where we have a lawful basis under UK GDPR.",
        },
      ],
    },
    {
      id: "legal-basis-for-processing",
      title: "Legal Basis for Processing",
      blocks: [
        { type: "paragraph", text: "We process your information because:" },
        {
          type: "list",
          items: [
            "You have requested information or a quotation.",
            "Processing is necessary to perform a contract.",
            "We have a legitimate business interest.",
            "We are legally required to retain certain information.",
          ],
        },
      ],
    },
    {
      id: "cookies",
      title: "Cookies",
      blocks: [
        { type: "paragraph", text: "Our website may use cookies to:" },
        {
          type: "list",
          items: [
            "Improve website functionality",
            "Analyse website traffic",
            "Remember user preferences",
          ],
        },
        {
          type: "paragraph",
          text: "Where required, visitors will be asked to consent to non-essential cookies.",
        },
        {
          type: "paragraph",
          text: "You can disable cookies through your browser settings at any time.",
        },
      ],
    },
    {
      id: "analytics",
      title: "Analytics",
      blocks: [
        {
          type: "paragraph",
          text: "We may use website analytics tools such as Google Analytics to understand how visitors use our website.",
        },
        { type: "paragraph", text: "These services may collect:" },
        {
          type: "list",
          items: [
            "IP address (anonymised where possible)",
            "Device information",
            "Browser information",
            "Pages visited",
            "Time spent on pages",
          ],
        },
        {
          type: "paragraph",
          text: "This information is used solely to improve our website.",
        },
      ],
    },
    {
      id: "marketing",
      title: "Marketing",
      blocks: [
        {
          type: "paragraph",
          text: "We will never send marketing emails unless you have requested them or have consented to receive them.",
        },
        { type: "paragraph", text: "You may unsubscribe at any time." },
      ],
    },
    {
      id: "sharing-your-information",
      title: "Sharing Your Information",
      blocks: [
        { type: "paragraph", text: "We do not sell your personal information." },
        {
          type: "paragraph",
          text: "We may share information with trusted third parties where necessary, including:",
        },
        {
          type: "list",
          items: [
            "Website hosting providers",
            "IT support providers",
            "Professional advisers",
            "Government authorities where required by law",
          ],
        },
        {
          type: "paragraph",
          text: "All third parties are required to protect your information.",
        },
      ],
    },
    {
      id: "data-security",
      title: "Data Security",
      blocks: [
        {
          type: "paragraph",
          text: "We take appropriate technical and organisational measures to protect your personal information against:",
        },
        {
          type: "list",
          items: ["Unauthorised access", "Loss", "Misuse", "Disclosure", "Alteration"],
        },
        {
          type: "paragraph",
          text: "Although we strive to protect your data, no method of internet transmission is completely secure.",
        },
      ],
    },
    {
      id: "data-retention",
      title: "Data Retention",
      blocks: [
        {
          type: "paragraph",
          text: "We retain personal information only for as long as necessary to:",
        },
        {
          type: "list",
          items: [
            "Respond to enquiries",
            "Deliver our services",
            "Meet legal and accounting obligations",
            "Resolve disputes",
          ],
        },
        {
          type: "paragraph",
          text: "When information is no longer required it is securely deleted.",
        },
      ],
    },
    {
      id: "your-rights",
      title: "Your Rights",
      blocks: [
        { type: "paragraph", text: "Under UK GDPR you have the right to:" },
        {
          type: "list",
          items: [
            "Access your personal data",
            "Correct inaccurate information",
            "Request deletion of your data",
            "Restrict processing",
            "Object to processing",
            "Request data portability",
            "Withdraw consent where applicable",
          ],
        },
        {
          type: "paragraph",
          text: "To exercise any of these rights please contact us using the details below.",
        },
      ],
    },
    {
      id: "third-party-websites",
      title: "Third-Party Websites",
      blocks: [
        { type: "paragraph", text: "Our website may contain links to other websites." },
        {
          type: "paragraph",
          text: "We are not responsible for the privacy practices of third-party websites and encourage you to read their privacy policies.",
        },
      ],
    },
    {
      id: "childrens-privacy",
      title: "Children's Privacy",
      blocks: [
        {
          type: "paragraph",
          text: "Our website is not intended for children under the age of 16.",
        },
        {
          type: "paragraph",
          text: "We do not knowingly collect personal information from children.",
        },
      ],
    },
    {
      id: "contact-us",
      title: "Contact Us",
      blocks: [
        {
          type: "paragraph",
          text: "If you have any questions about this Privacy Policy or how your personal information is handled, please contact:",
        },
        {
          type: "address",
          contact: { ...legalContact, lines: legalContact.lines.slice(0, 3) },
        },
      ],
    },
    {
      id: "complaints",
      title: "Complaints",
      blocks: [
        {
          type: "paragraph",
          text: "If you are unhappy with how we process your information, you have the right to lodge a complaint with the UK's supervisory authority:",
        },
        {
          type: "callout",
          title: icoContact.name,
          blocks: [
            {
              type: "paragraph",
              text: [
                "Website: ",
                { text: icoContact.website, href: icoContact.website, external: true },
              ],
            },
            {
              type: "paragraph",
              text: [
                "Telephone: ",
                { text: icoContact.telephone, href: icoContact.telephoneHref },
              ],
            },
          ],
        },
      ],
    },
    {
      id: "changes-to-this-policy",
      title: "Changes to This Policy",
      blocks: [
        {
          type: "paragraph",
          text: "We may update this Privacy Policy from time to time.",
        },
        {
          type: "paragraph",
          text: "Any updates will be published on this page with a revised effective date.",
        },
      ],
    },
  ],
};

export default privacyPolicy;
